import { useContext, useEffect, useState } from "react"
import { Field, FastField, useField } from "formik"

import { FormContext } from "../../context/formContext"
import { InputTextarea } from "primereact/inputtextarea"

import styles from "../../styles/Form.module.css"
import fieldVisibility from "../../helpers/fieldVisibility"

// Helpers
import checkIfRequired from "./helpers/checkIfRequired"
import RewriteText from "../GeminiAI/RewriteText"

function RegularFieldOutput(props) {
  const { field, isRequired, block, index } = props
  const form = useContext(FormContext)
  const [, , helpers] = useField(field.field_name)
  const [isRewriting, setIsRewriting] = useState(false)
  const isDisabled = (form ? form.blocksSaved[block] : false) || field.read_only || isRewriting

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <Field name={field.field_name}>
        {({ field: formikField, meta }) => (
          <div className={styles.textAreaContainer}>
            <InputTextarea
              name={formikField.name}
              value={formikField.value}
              onChange={formikField.onChange}
              disabled={isDisabled}
              rows={3}
              className={styles.textarea}
              onBlur={formikField.onBlur}
            />
            {meta.touched && meta.error && (
              <div className={styles.errFieldMsg}>{meta.error}</div>
            )}
            <RewriteText
              text={formikField.value}
              additionalPrompt={"retain line breaks where needed"}
              className={styles.textAreaAiButton}
              tooltipPosition={"left"}
              onStartRewrite={() => {
                setIsRewriting(true)
              }}
              onCompleteRewrite={(newValue) => {
                if (newValue) {
                  helpers.setValue(newValue)
                }
                setIsRewriting(false)
              }}
            />
          </div>
        )}
      </Field>
    </div>
  )
}

function MultiFieldOutput(props) {
  const { field, index, mfFieldName, mfIndex } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`
  const isRequired = checkIfRequired(props.field.field_validation.required[0])

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, meta }) => {
            return (
              <>
                <InputTextarea
                  name={fastField.name}
                  value={fastField.value}
                  onChange={fastField.onChange}
                  rows={3}
                  className={styles.textarea}
                  onBlur={fastField.onBlur}
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}

function NestedMultiFieldOutput(props) {
  const {
    field,
    index,
    mfFieldName,
    mfIndex,
    nestedMfFieldName,
    nestedMfIndex,
  } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${nestedMfFieldName}.${nestedMfIndex}.${field.field_name}`
  const isRequired = checkIfRequired(props.field.field_validation.required[0])

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, meta }) => {
            return (
              <>
                <InputTextarea
                  name={fastField.name}
                  id={fastField.name}
                  value={fastField.value}
                  onChange={fastField.onChange}
                  rows={3}
                  className={styles.textarea}
                  onBlur={fastField.onBlur}
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}

export default function TypeTextarea(props) {
  const form = useContext(FormContext)

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(props.field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(props.field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  switch (props.type) {
    case "regular-field":
      const regFieldProps = {
        field: props.field,
        isRequired: checkIfRequired(props.field.field_validation.required[0]),
        block: props.block,
        index: props.index,
      }
      return <RegularFieldOutput {...regFieldProps} />
    case "multi-field":
      const multiFieldProps = {
        field: props.field,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
      }
      return <MultiFieldOutput {...multiFieldProps} />
    case "nested-multi-field":
      const nestedMultiFieldProps = {
        field: props.field,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
        nestedMfFieldName: props.nestedMfFieldName,
        nestedMfIndex: props.nestedMfIndex,
      }
      return <NestedMultiFieldOutput {...nestedMultiFieldProps} />
    default:
      return null
  }
}
